import { inject, injectable } from 'inversify'
import type {
  IRecipientContactProperties,
  IRecipientListProperties,
  MakeRecipientOptionsParams,
  MessagesPricePostBody,
  Recipient,
  RecipientAvatar,
  RecipientOption,
  RecipientType,
  TInteractiveRecipientOption,
} from '@/services/domain/messages/types'
import { addPlusToPhoneNumber } from '@/utils/string/addPlusToPhoneNumber'
import { SERVICE_TYPES } from '@/core/container/types'
import type ContactService from '@/services/domain/contact/contactService'
import type { DistributionRecipients } from '@/data/models/domain/services/distribution/DistributionDetails'
import type ContactListService from '@/services/domain/contactList/contactListService'
import type NumericService from '@/services/numericService'
import type PhoneService from '@/services/phoneService'
import { safeParseInt } from '@/utils/number'
import type FilteredView from '@/data/models/domain/filteredViews/FilteredView'
import type ContactListFolder from '@/data/models/domain/contactGrouping/ContactListFolder'
import type Contact from '@/data/models/domain/Contact'
import { getHashCode } from '@/utils/hash'
import type { SegmentsService } from '@/services/domain/contact/segments/segmentsService'
import type MessagesPriceService from '@/services/domain/messages/messagesPriceService'
import type { RecipientDataMapperService } from '@/services/domain/messages/recipientDataMapperService'
import TmLogicError from '@/core/error/tmLogicError'
import TmAbsurdError from '@/core/error/tmAbsurdError'
import type { FeatureFlagsService } from '@/services/featureFlagsService'
import { FeatureFlags } from '@/services/types'

@injectable()
export default class RecipientService {
  constructor(
    @inject(SERVICE_TYPES.ContactService) protected readonly contactService: ContactService,
    @inject(SERVICE_TYPES.ContactListService) protected readonly contactListService: ContactListService,
    @inject(SERVICE_TYPES.NumericService) protected readonly numericService: NumericService,
    @inject(SERVICE_TYPES.PhoneService) protected readonly phoneService: PhoneService,
    @inject(SERVICE_TYPES.SegmentsService) protected readonly segmentsService: SegmentsService,
    @inject(SERVICE_TYPES.MessagesPriceService) protected readonly messagesPriceService: MessagesPriceService,
    @inject(SERVICE_TYPES.RecipientDataMapperService)
    protected readonly recipientDataMapperService: RecipientDataMapperService,
    @inject(SERVICE_TYPES.FeatureFlagsService) protected readonly featureFlagsService: FeatureFlagsService,
  ) {}

  private getUid(el: Pick<Recipient, 'entityId'>) {
    return el.entityId.toString()
  }

  private getAvatarPropsForContact(
    el: Pick<Recipient, 'entityType' | 'entityId' | 'title' | 'value' | 'avatarHref'>,
  ): RecipientAvatar {
    return this.contactService.getAvatarPropsForContact<true>({
      id: String(el.entityId),
      phone: el.entityType === 'reply_email' ? '' : (el.value ?? ''),
      email: el.entityType === 'reply_email' ? (el.value ?? '') : '',
      avatarHref: el.avatarHref ?? undefined,
      fullName: el.entityType === 'reply' || el.entityType === 'reply_email' ? '' : el.title,
      firstName: el.value,
      isList: el.entityType === 'list',
      isFilteredView: el.entityType === 'filtered_view',
    })
  }

  public normalizeRecipientForContact(el: Omit<Recipient<'contact'>, 'countryName'>): TInteractiveRecipientOption {
    const recipient: TInteractiveRecipientOption = {
      ...el,
      uid: this.getUid(el),
      entityId: el.entityId || safeParseInt(el.value ?? ''),
      avatar: this.getAvatarPropsForContact(el),
      nameAsPhone: el.title.trim() === el.value,
      label: el.title,
      hint: this.phoneService.preparePhoneForDisplay(el.value ?? ''),
    }
    return recipient
  }

  public normalizeRecipientForContactEmail(
    el: Omit<Recipient<'contact_email'>, 'countryName'>,
  ): TInteractiveRecipientOption {
    const recipient: TInteractiveRecipientOption = {
      ...el,
      uid: this.getUid(el),
      entityId: el.entityId || safeParseInt(el.value ?? ''),
      avatar: this.getAvatarPropsForContact(el),
      nameAsPhone: el.title.trim() === el.value,
      label: el.title,
      hint: el.value ?? undefined,
    }
    return recipient
  }

  public normalizeRecipientForList(el: Omit<Recipient<'list'>, 'countryName'>): TInteractiveRecipientOption {
    const count = this.formatRecipientsCount(el.value ?? '')
    const recipient: TInteractiveRecipientOption = {
      ...el,
      uid: this.getUid(el),
      entityId: el.entityId || safeParseInt(el.value ?? ''),
      avatar: this.getAvatarPropsForContact(el),
      label: el.title,
      hint: `${count} recipients`,
      shortHint: count,
      isTemporary: false,
    }

    return recipient
  }

  public normalizeRecipientForFilteredView(
    el: Omit<Recipient<'filtered_view'>, 'countryName'>,
  ): TInteractiveRecipientOption {
    const count = this.formatRecipientsCount(el.value ?? '')
    const recipient: TInteractiveRecipientOption = {
      ...el,
      sharedBy: undefined,
      uid: this.getUid(el),
      entityId: el.entityId || safeParseInt(el.value ?? ''),
      avatar: this.getAvatarPropsForContact(el),
      label: el.title,
      hint: `${count} recipients`,
      shortHint: count,
    }
    return recipient
  }

  public normalizeRecipientForReply(el: Recipient<'reply'>): TInteractiveRecipientOption {
    const recipient: TInteractiveRecipientOption = {
      ...el,
      uid: this.getUid(el),
      entityId: el.entityId || safeParseInt(el.value ?? ''),
      avatar: this.getAvatarPropsForContact(el),
      nameAsPhone: true,
      label: this.phoneService.preparePhoneForDisplay(el.value ?? ''),
      hint: el.countryName,
    }

    return recipient
  }

  public normalizeRecipientForReplyEmail(el: Recipient<'reply_email'>): TInteractiveRecipientOption {
    let label: string
    if (el.value) {
      label = el.value
    } else {
      throw new TmLogicError('"label" is null')
    }
    const recipient: TInteractiveRecipientOption = {
      ...el,
      uid: this.getUid(el),
      entityId: el.entityId || safeParseInt(el.value ?? ''),
      avatar: this.getAvatarPropsForContact(el),
      label,
    }
    return recipient
  }

  public normalizeRecipient(el: Recipient<Exclude<RecipientType, 'email'>>): TInteractiveRecipientOption {
    if (el.entityType === 'list') {
      return this.normalizeRecipientForList({
        ...el,
        entityType: el.entityType,
      })
    }
    if (el.entityType === 'filtered_view') {
      return this.normalizeRecipientForFilteredView({
        ...el,
        entityType: el.entityType,
      })
    }
    if (el.entityType === 'contact') {
      return this.normalizeRecipientForContact({
        ...el,
        entityType: el.entityType,
      })
    }
    if (el.entityType === 'contact_email') {
      return this.normalizeRecipientForContactEmail({
        ...el,
        entityType: el.entityType,
      })
    }
    if (el.entityType === 'reply') {
      return this.normalizeRecipientForReply({
        ...el,
        entityType: el.entityType,
      })
    }
    if (el.entityType === 'reply_email') {
      return this.normalizeRecipientForReplyEmail({
        ...el,
        entityType: el.entityType,
      })
    }
    throw new TmAbsurdError(el.entityType)
  }

  public normalizeRecipients(recipients: Recipient<Exclude<RecipientType, 'email'>>[]) {
    return recipients.map((el) => {
      return this.normalizeRecipient(el)
    })
  }

  public normalizeRecipientOptions(options: RecipientOption[]): Required<DistributionRecipients> {
    const groupIds: number[] = []
    const contactIds: number[] = []
    const segmentIds: number[] = []
    const folderIds: number[] = []
    const numbers: string[] = []
    const emails: string[] = []

    options.forEach((el) => {
      if (el.entityType === 'list') {
        groupIds.push(el.entityId!)
      } else if (el.entityType === 'contact' || el.entityType === 'contact_email') {
        contactIds.push(el.entityId!)
      } else if (el.entityType === 'filtered_view') {
        segmentIds.push(el.entityId!)
      } else if (el.entityType === 'folder') {
        folderIds.push(el.entityId)
      } else if (el.entityType === 'email' || el.entityType === 'reply_email') {
        if (!el.value) {
          throw new TmLogicError('el.value is null')
        }
        emails.push(el.value)
      } else {
        if (!el.value) {
          throw new TmLogicError('el.value is null')
        }
        numbers.push(addPlusToPhoneNumber(el.value))
      }
    })

    return {
      groupIds,
      contactIds,
      segmentIds,
      folderIds,
      numbers,
      emails,
    }
  }

  public prepareRecipientsForClipboard(recipients: RecipientOption[]) {
    const separator = '\n'

    const values = recipients
      .reduce((acc, el: RecipientOption) => {
        if (el.entityType !== 'contact') {
          acc += `${el.label}${separator}`
        } else if (el.value) {
          acc += `${this.phoneService.clearPhoneAndAddPlus(el.value)}${separator}`
        }
        return acc
      }, '')
      .slice(0, -1)

    return values
  }

  public async makeRecipientOptions(
    recipients: DistributionRecipients,
    options: Partial<MakeRecipientOptionsParams> = {},
  ): Promise<RecipientOption[]> {
    const contactsIds = recipients.contactIds.map((contactId) => contactId.toString())
    const contactListsIds = recipients.groupIds.map((listId) => listId.toString())
    const segmentsIds = recipients.segmentIds.map((segmentId) => segmentId.toString())

    const [contacts, contactLists, segments] = await Promise.all([
      this.contactService.fillEntitiesByIds(contactsIds),
      this.contactListService.fillEntitiesByIds(contactListsIds, []),
      this.segmentsService.fillEntitiesByIds(segmentsIds),
    ])

    let filteredContacts = contacts
    if (options.removeBlockedContacts) {
      filteredContacts = filteredContacts.filter((t) => !t.blocked)
    }
    const contactOptions = filteredContacts.map((contact) => this.makeRecipientOptionFromContact(contact))
    const contactListOptions = contactLists.map((contactList) => this.makeRecipientOptionFromContactList(contactList))
    const segmentsOptions = segments
      .map((segment) => this.makeRecipientOptionFromFilteredView(segment))
      .filter((option) => option !== null)
    const phoneNumberOptions = recipients.numbers.map((phoneNumber) =>
      this.makeRecipientOptionFromPhoneNumber(phoneNumber),
    )
    const emailsOptions = recipients.emails?.map((email) => this.makeRecipientOptionFromEmail(email)) ?? []

    return [...contactOptions, ...contactListOptions, ...phoneNumberOptions, ...emailsOptions, ...segmentsOptions]
  }

  public getRequestParamsFromRecipients(recipients: RecipientOption[]) {
    const { groupIds, folderIds, contactIds, segmentIds, numbers } = this.normalizeRecipientOptions(recipients)

    const toReturn: Pick<MessagesPricePostBody, 'lists' | 'folders' | 'contacts' | 'filteredViews' | 'phones'> = {
      lists: groupIds.join(','),
      contacts: contactIds.join(','),
      filteredViews: segmentIds.join(','),
      phones: numbers.join(','),
    }

    if (this.featureFlagsService.isFeatureEnabled(FeatureFlags.Folders)) {
      toReturn.folders = folderIds.join(',')
    }

    return toReturn
  }

  public async getRecipientsCount(recipients: RecipientOption[]): Promise<number> {
    return this.messagesPriceService.fetchRecipientsCount({
      ...this.getRequestParamsFromRecipients(recipients),
      text: '',
      from: null,
    })
  }

  public makeRecipientOptionFromFolder(folder: Pick<ContactListFolder, 'id' | 'name'>): RecipientOption {
    return {
      entityType: 'folder',
      label: folder.name,
      value: folder.id,
      entityId: parseInt(folder.id, 10),
    }
  }

  public makeRecipientOptionFromContactList(list: IRecipientListProperties): RecipientOption {
    return {
      entityType: 'list',
      label: list.name,
      value: `${list.id}`,
      entityId: parseInt(list.id, 10),
      isTemporary: list.isTemporary,
      hint: this.recipientDataMapperService.formatMembersCount(list.membersCount),
    }
  }

  public makeRecipientOptionFromFilteredView(
    filteredView: Pick<FilteredView, 'id' | 'name' | 'count'>,
  ): RecipientOption {
    return {
      entityType: 'filtered_view',
      label: filteredView.name,
      value: `${filteredView.id}`,
      hint: this.recipientDataMapperService.formatMembersCount(filteredView.count),
      entityId: parseInt(filteredView.id, 10),
    }
  }

  public makeRecipientOptionFromPhoneNumber(phoneNumber: string): RecipientOption {
    return {
      entityId: safeParseInt(phoneNumber),
      entityType: 'raw',
      label: this.recipientDataMapperService.getPhoneLabel(phoneNumber),
      value: `${phoneNumber}`.replace(/^\+/, ''),
    }
  }

  public makeRecipientOptionFromEmail(email: string): RecipientOption {
    return {
      entityId: getHashCode(email),
      entityType: 'email',
      label: this.recipientDataMapperService.getEmailLabel(email),
      value: email,
    }
  }

  public makeRecipientOptionFromContact(contact: IRecipientContactProperties): RecipientOption {
    const label = contact.fullName?.trim() ?? ''
    return {
      entityType: 'contact',
      label,
      value: contact.phone ?? '',
      entityId: parseInt(contact.id, 10),
      hint: this.recipientDataMapperService.computeContactHint(contact),
    }
  }

  public makeRecipientOptionFromContactEmail(contact: Pick<Contact, 'id' | 'email' | 'fullName'>): RecipientOption {
    const label = contact.fullName?.trim() ?? ''

    return {
      entityType: 'contact_email',
      label,
      value: contact.email ?? '',
      entityId: parseInt(contact.id, 10),
      hint: contact.email ?? '',
    }
  }

  public makeRecipientOptionFromReplyEmail(email: string): RecipientOption {
    return this.makeRecipientOptionFromEmail(email)
  }

  private formatRecipientsCount(count: string): string {
    const num = Number(count)
    if (!Number.isNaN(num)) {
      return this.numericService.formatInteger(num)
    }
    return count
  }
}
